import React from 'react';

import { FormattedMessage } from 'react-intl';

import BranchesIcon from '@atlaskit/icon/glyph/bitbucket/branches';
import CommitsIcon from '@atlaskit/icon/glyph/bitbucket/commits';
import PullRequestsIcon from '@atlaskit/icon/glyph/bitbucket/pullrequests';
import UploadIcon from '@atlaskit/icon/glyph/upload';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import { useIntl } from 'src/hooks/intl';
import { DevActivityKind } from 'src/redux/jira/constants';
import { DevActivity } from 'src/redux/jira/types';

import messages from './dev-activity-icon.i18n';

type Props = {
  devActivity: DevActivity;
};

// Icon to show as part of a Jira issue (e.g. a row in a table) to indicate
// whether it has any linked "dev activity" such as commits/branches/pull requests.
export const DevActivityIcon = ({ devActivity }: Props) => {
  const intl = useIntl();

  const { dataType, count, open, state } = devActivity;
  if (!count) {
    return null;
  }

  let icon;
  let message;
  switch (dataType) {
    case DevActivityKind.Branch:
      message = messages.devStatusOpenBranches;
      icon = (
        <BranchesIcon
          size="small"
          label={intl.formatMessage(message, { count })}
        />
      );
      break;
    case DevActivityKind.Commit:
      message = messages.devStatusCommits;
      icon = (
        <CommitsIcon
          size="small"
          label={intl.formatMessage(message, { count })}
        />
      );
      break;
    case DevActivityKind.PullRequest:
      if (open) {
        message = messages.devStatusPullRequests;
        icon = (
          <PullRequestsIcon
            size="small"
            label={intl.formatMessage(message, { count })}
          />
        );
      } else {
        if (state === 'DECLINED') {
          message = messages.devStatusDeclinedPullRequests;
          icon = (
            <PullRequestsIcon
              size="small"
              label={intl.formatMessage(message, { count })}
              primaryColor={token('color.icon.danger', colors.R300)}
            />
          );
        } else {
          message = messages.devStatusMergedPullRequests;
          icon = (
            <PullRequestsIcon
              size="small"
              label={intl.formatMessage(message, { count })}
              primaryColor={token('color.icon.success', colors.G300)}
            />
          );
        }
      }
      break;
    case DevActivityKind.Deployment:
      message = messages.devStatusDeployments;
      icon = (
        <UploadIcon
          size="small"
          label={intl.formatMessage(message, { count })}
        />
      );
      break;
  }

  if (icon && message) {
    return (
      <Tooltip
        content={
          <FormattedMessage
            {...message}
            values={{
              count,
            }}
          />
        }
        position="bottom"
      >
        {icon}
      </Tooltip>
    );
  }

  return null;
};
